/**
 * BrandLogoUploader
 * ─────────────────
 * Drop-anywhere component for uploading a logo to a brand
 * (`local_business_listings`) that doesn't have one yet. Wired into
 * the campaign wizard so operators can fix the "letter-fallback"
 * avatar inline instead of bouncing out to the Listings admin tab.
 *
 * Flow:
 *   1. User picks an image (PNG / JPEG / WEBP / SVG, ≤2 MB).
 *   2. We upload it to the `business-logos` public bucket under
 *      `<business_id>/<timestamp>-<random>.<ext>`.
 *   3. The resulting public URL is patched onto
 *      `local_business_listings.logo_url` via `adminApi.updateListing`
 *      (which auto-audits the change).
 *   4. We invoke `onUploaded(publicUrl)` so the caller can keep its
 *      local copy of the brand in sync (wizard store, gallery refresh
 *      callback, etc.).
 *
 * The component is intentionally headless-ish: callers pass their own
 * trigger button rendered against the existing `currentLogoUrl`. We
 * just own the file input + the upload network round-trip.
 */

import { useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { adminApi } from '@/lib/adminApi';
import { Button } from '@/components/ui/button';
import { Loader2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { optimizeImageForUpload, LOGO_PRESET } from '@/lib/imageOptimize';

const BUCKET = 'business-logos';
const MAX_BYTES = 2 * 1024 * 1024; // matches the bucket's file_size_limit
const ACCEPTED_MIMES = new Set([
  'image/png', 'image/jpeg', 'image/webp', 'image/svg+xml',
]);

export interface BrandLogoUploaderProps {
  businessId: string;
  businessName: string;
  /** Existing logo (null if the brand is on the letter fallback). */
  currentLogoUrl: string | null;
  /** Fires once the upload + DB patch succeed. Receives the public URL. */
  onUploaded: (publicUrl: string) => void;
  /**
   * Visual style of the affordance:
   *   - `pill`  → small button suitable for the wizard header strip
   *   - `tile`  → square card matching the Logo Gallery grid
   *   - `inline`→ just text + paperclip icon, for dense rows
   */
  variant?: 'pill' | 'tile' | 'inline';
  /** Optional override label. Defaults are variant-specific. */
  label?: string;
  /** Disabled state (e.g. when not yet authenticated as admin). */
  disabled?: boolean;
}

export function BrandLogoUploader({
  businessId,
  businessName,
  currentLogoUrl,
  onUploaded,
  variant = 'pill',
  label,
  disabled = false,
}: BrandLogoUploaderProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);

  const handlePick = () => {
    if (disabled || uploading) return;
    inputRef.current?.click();
  };

  const handleFile = async (file: File) => {
    if (!file) return;

    if (!ACCEPTED_MIMES.has(file.type)) {
      toast.error('Logo must be a PNG, JPEG, WEBP, or SVG file.');
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error(`Logo is too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Max 2 MB.`);
      return;
    }

    setUploading(true);
    try {
      const optimized = await optimizeImageForUpload(file, LOGO_PRESET);
      const ext = optimized.name.split('.').pop()?.toLowerCase() || 'png';
      const path = `${businessId}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;

      const { error: uploadErr } = await supabase.storage
        .from(BUCKET)
        .upload(path, optimized, {
          contentType: optimized.type,
          upsert: false,
          // 1 year cache; URL is unique per upload so a stale cache
          // never serves the wrong logo.
          cacheControl: '31536000',
        });
      if (uploadErr) throw uploadErr;

      const { data: urlData } = supabase.storage
        .from(BUCKET)
        .getPublicUrl(path);

      // Patch the brand row so the new logo flows everywhere this
      // brand is rendered (gallery, pin card, festival pill, etc.).
      // updateListing already audits the change.
      await adminApi.updateListing(businessId, { logo_url: urlData.publicUrl });

      onUploaded(urlData.publicUrl);
      toast.success(`Uploaded logo for ${businessName}`, {
        description: 'Saved on the brand record. Every future campaign + the in-app pin card will use it automatically.',
      });
    } catch (err: any) {
      toast.error(err?.message || 'Logo upload failed.');
    } finally {
      setUploading(false);
      // Reset the input so the same filename can be picked twice in a row.
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  // ── Render variants ────────────────────────────────────────────

  const buttonLabel =
    label ?? (currentLogoUrl ? 'Replace logo' : 'Upload logo');

  if (variant === 'tile') {
    return (
      <>
        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/svg+xml"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) handleFile(f);
          }}
        />
        <button
          type="button"
          onClick={handlePick}
          disabled={disabled || uploading}
          className="flex flex-col items-center gap-1.5 p-2 rounded-lg border border-dashed border-emerald-500/40 bg-emerald-500/5 hover:bg-emerald-500/10 transition-colors disabled:opacity-50"
          title={`Upload a logo for ${businessName}`}
        >
          <div className="w-12 h-12 rounded-full border border-emerald-500/30 bg-emerald-500/10 flex items-center justify-center">
            {uploading
              ? <Loader2 className="w-5 h-5 text-emerald-300 animate-spin" />
              : <Upload className="w-5 h-5 text-emerald-300" />}
          </div>
          <span className="text-[10px] text-emerald-200 text-center leading-tight">
            {uploading ? 'Uploading…' : buttonLabel}
          </span>
        </button>
      </>
    );
  }

  if (variant === 'inline') {
    return (
      <>
        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/svg+xml"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) handleFile(f);
          }}
        />
        <button
          type="button"
          onClick={handlePick}
          disabled={disabled || uploading}
          className="text-[11px] text-emerald-300 hover:text-emerald-200 underline underline-offset-2 disabled:opacity-50 inline-flex items-center gap-1"
        >
          {uploading
            ? <Loader2 className="w-3 h-3 animate-spin" />
            : <Upload className="w-3 h-3" />}
          {uploading ? 'Uploading…' : buttonLabel}
        </button>
      </>
    );
  }

  // Default: pill button
  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/svg+xml"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) handleFile(f);
        }}
      />
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={handlePick}
        disabled={disabled || uploading}
        className="text-xs border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 hover:text-emerald-200"
        title={`Upload a logo for ${businessName}`}
      >
        {uploading
          ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
          : <Upload className="w-3.5 h-3.5 mr-1.5" />}
        {uploading ? 'Uploading…' : buttonLabel}
      </Button>
    </>
  );
}
